import React from "react";
import { Icon, notify, confirmDialog } from "../registry";
import type { SiteContent, FenceStyle, CityPage, Post, Day, OpeningHours, PageKey } from "./types";
import { getIn, slugify, toE164, COLOR_PRESETS, COLOR_FIELDS, FONTS, printKitColors, PAGE_LIST, longDate, stable } from "./util";
import {
  WsCtx, OFFLINE_MSG, Field, TextField, NumField, TextArea, Toggle, SelectField, Group, StringListEditor, ObjListEditor,
  CardListEditor, StepListEditor, FaqEditor, ChipsInput, PhotoField, PhotoListEditor, BlockEditor, AiButton, uploadImage,
} from "./fields";
import { EarnedCities } from "./earned";

/* ============================================================
   Website builder — one component per section tab. Each gets:
     d      the draft SiteContent
     set    (path, value) → immutable update + autosave
     bind   (path) → { value, onChange } for the field editors
     ai     ({ section, label, current, answers, apply }) → AI rewrite modal
     setSub (slug|null) → which sub-page the preview shows
   ============================================================ */

export type TabProps = {
  d: SiteContent; set: (p: any, v: any) => void; bind: (p: any) => { value: any; onChange: (v: any) => void };
  ai: (req: any) => void; aiBusy: string; setSub: (s: string | null) => void; sub: string | null;
  store: any; go: any; live: boolean;
};

function TabHead({ title, sub, children }: any) {
  return (
    <div className="ws-tabhead">
      <div style={{ minWidth: 0 }}><h2 className="display">{title}</h2>{sub && <p className="muted">{sub}</p>}</div>
      {children && <div className="row gap-8" style={{ flexWrap: "wrap" }}>{children}</div>}
    </div>
  );
}
const two = (a: any, b: any) => <div className="ws-2">{a}{b}</div>;

/* ---------------- Business ---------------- */
const DAYS: Day[] = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
function HoursEditor({ value, onChange }: { value: OpeningHours[]; onChange: (v: OpeningHours[]) => void }) {
  const rows = Array.isArray(value) ? value : [];
  const set = (i: number, p: Partial<OpeningHours>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  return (
    <Field label="Opening hours (for Google)" hint="Structured hours feed your site's business listing data. Leave a day out if you're closed.">
      <div className="ws-list">
        {rows.map((r, i) => (
          <div key={i} className="ws-hours">
            <div className="ws-days">{DAYS.map((dd) => {
              const on = (r.days || []).includes(dd);
              return <button key={dd} type="button" className={"ws-day" + (on ? " on" : "")} aria-pressed={on}
                onClick={() => set(i, { days: DAYS.filter((x) => (x === dd ? !on : (r.days || []).includes(x))) })}>{dd}</button>;
            })}</div>
            <div className="row gap-8" style={{ alignItems: "center" }}>
              <input className="input ws-input" type="time" aria-label="Opens" value={r.opens} onChange={(e: any) => set(i, { opens: e.target.value })} />
              <span className="faint">to</span>
              <input className="input ws-input" type="time" aria-label="Closes" value={r.closes} onChange={(e: any) => set(i, { closes: e.target.value })} />
              <button type="button" className="ws-ib danger" aria-label="Remove hours row" onClick={() => onChange(rows.filter((_, j) => j !== i))}><Icon name="x" size={14} /></button>
            </div>
          </div>
        ))}
        <button type="button" className="ws-add" onClick={() => onChange([...rows, { days: rows.length ? ["Sa"] : ["Mo", "Tu", "We", "Th", "Fr"], opens: "08:00", closes: rows.length ? "12:00" : "17:00" }])}><Icon name="plus" size={13} />Add hours</button>
      </div>
    </Field>
  );
}
function PhoneField({ label, value, onChange, hint, allowClear }: any) {
  const v = value || { display: "", e164: "" };
  return (
    <Field label={label} hint={hint || (v.display ? "Dials as " + (toE164(v.display) || "—") : null)} right={allowClear && value ? <button type="button" className="rv-link" onClick={() => onChange(undefined)}>Remove</button> : null}>
      <input className="input ws-input" type="tel" value={v.display} placeholder="(555) 555-0100" onChange={(e: any) => onChange({ display: e.target.value, e164: toE164(e.target.value) })} />
    </Field>
  );
}

export function BusinessTab({ d, set, bind, store }: TabProps) {
  const settings = (store && store.settings) || {};
  const token = settings.widget && settings.widget.token;
  const gtm = d.integrations.gtmId || "";
  return (
    <>
      <TabHead title="Business" sub="The facts every page uses — name, phone, hours, and the claims your site is allowed to make." />
      <Group title="Name">
        {two(<TextField label="Business name" {...bind("business.name")} />, <TextField label="Short name" hint="Header, logo text, page titles" {...bind("business.shortName")} />)}
        {two(<TextField label="Slogan" placeholder="Built to last." {...bind("business.slogan")} />, <TextField label="Tagline" placeholder="Fences built to outlast the winters" {...bind("business.tagline")} />)}
        {two(<TextField label="Owner" {...bind("business.ownerName")} />, <NumField label="Year founded" value={d.business.yearFounded} onChange={(v: number) => set("business.yearFounded", v || undefined)} />)}
      </Group>
      <Group title="Phone & email">
        {two(<PhoneField label="Main phone" value={d.business.phone} onChange={(v: any) => set("business.phone", v)} />,
          d.business.textPhone ? <PhoneField label="Texting line" allowClear value={d.business.textPhone} onChange={(v: any) => set("business.textPhone", v)} />
            : <Field label="Texting line"><button type="button" className="btn btn-ghost btn-sm" onClick={() => set("business.textPhone", { display: "", e164: "" })}><Icon name="plus" size={13} />Add a separate texting number</button></Field>)}
        <TextField label="Email" type="email" {...bind("business.email")} />
      </Group>
      <Group title="Address">
        <TextField label="Street" {...bind("business.address.street")} />
        <div className="ws-3">
          <TextField label="City" {...bind("business.address.city")} />
          <TextField label="State" {...bind("business.address.region")} />
          <TextField label="ZIP" {...bind("business.address.postal")} />
        </div>
        <Toggle label="Show my street address on the site" hint="Leave off if you work from home — the site shows your city and service area instead." {...bind("business.address.showStreet")} />
      </Group>
      <Group title="Hours">
        <TextField label="Hours, as shown on the site" placeholder="Mon–Fri 7:00am – 5:00pm" {...bind("business.hoursText")} />
        <TextField label="By appointment" placeholder="Sat by appointment" {...bind("business.byAppointment")} />
        <HoursEditor value={d.business.hours} onChange={(v) => set("business.hours", v)} />
      </Group>
      <Group title="Service area & credentials">
        <TextField label="Service area line" placeholder="Green Bay & Northeast Wisconsin" {...bind("business.serviceAreaText")} />
        <TextField label="License #" hint="Shown only when filled in. The “Licensed” badge needs it." {...bind("business.license")} />
        <Toggle label="We carry liability insurance" hint="Turns on the “Insured” badge." {...bind("business.insured")} />
      </Group>
      <Group title="Claims" sub="shown only when true">
        <p className="ws-note">Your site only says what you tell it here. Nothing defaults to another company's promises.</p>
        <Toggle label="We pull permits and call 811 before digging" {...bind("claims.permitsHandled")} />
        <TextField label="Your state's 811 service" placeholder="Diggers Hotline (811)" {...bind("claims.locateService")} />
        <TextField label="Warranty" placeholder="5-year written workmanship warranty" {...bind("claims.warrantyText")} />
        <TextArea label="How you set posts" rows={2} placeholder="Galvanized steel posts set in concrete below the frost line" {...bind("claims.postMethod")} />
      </Group>
      <Group title="Quote widget, tracking & social" defaultOpen={false}>
        {two(
          <SelectField label="Chat & quote buttons" {...bind("integrations.widget")} options={[["both", "Chat + instant quote"], ["quote", "Instant quote only"], ["chat", "Chat only"], ["none", "None"]]} />,
          <Field label="Button color"><div className="row gap-8"><input type="color" className="ws-color" value={d.integrations.widgetColor || d.brand.colors.accent} onChange={(e: any) => set("integrations.widgetColor", e.target.value)} />
            {d.integrations.widgetColor && <button type="button" className="rv-link" onClick={() => set("integrations.widgetColor", undefined)}>Use accent</button>}</div></Field>,
        )}
        <Field label="Fence Works widget token" hint="Connects the chat, the instant quote and your live reviews feed to this account.">
          <div className="row gap-8"><input className="input ws-input" readOnly value={d.integrations.fenceworksToken || ""} placeholder="Not connected" />
            {token && token !== d.integrations.fenceworksToken && <button type="button" className="btn btn-ghost btn-sm" onClick={() => set("integrations.fenceworksToken", token)}>Use mine</button>}</div>
        </Field>
        <TextField label="Google review link" placeholder="https://g.page/r/…/review" {...bind("integrations.googleReviewUrl")}
          hint={settings.googleReviewUrl && settings.googleReviewUrl !== d.integrations.googleReviewUrl ? <button type="button" className="rv-link" onClick={() => set("integrations.googleReviewUrl", settings.googleReviewUrl)}>Use the one from Settings</button> : null} />
        <TextField label="Google Tag Manager ID" placeholder="GTM-XXXXXXX" {...bind("integrations.gtmId")} hint={gtm && !/^GTM-[A-Z0-9]{4,10}$/.test(gtm) ? <span style={{ color: "var(--amber)" }}>That doesn't look like a GTM container ID (GTM-XXXXXXX).</span> : "Optional — for Google Analytics / Ads tracking."} />
        {two(<TextField label="Facebook" placeholder="https://facebook.com/…" {...bind("integrations.social.facebook")} />, <TextField label="Instagram" placeholder="https://instagram.com/…" {...bind("integrations.social.instagram")} />)}
        {two(<TextField label="Google profile" placeholder="https://maps.google.com/…" {...bind("integrations.social.google")} />, <TextField label="Nextdoor" {...bind("integrations.social.nextdoor")} />)}
        <TextField label="YouTube" {...bind("integrations.social.youtube")} />
      </Group>
      <Group title="Legal pages" defaultOpen={false}>
        <p className="ws-note">Your privacy policy and terms are written from your business details. You're the site operator; Fence Works is named as the service provider that handles form submissions.</p>
        {two(<TextField label="Governing state" {...bind("legal.governingState")} />, <TextField label="Last updated" type="date" {...bind("legal.lastUpdated")} />)}
      </Group>
    </>
  );
}

/* ---------------- Design ---------------- */
const LOGO_SLOTS: [string, string, string, boolean][] = [
  ["onDark", "Logo on dark", "Header & footer — a light or white version reads best", true],
  ["onLight", "Logo on light", "Light sections and printouts", false],
  ["mark", "Icon", "Square mark for the browser tab and phone home screens", false],
  ["onDarkStacked", "Tall footer logo", "Optional — a stacked version with your tagline", true],
];
function LogoSlot({ k, label, hint, dark, value, onChange }: any) {
  const { live } = React.useContext(WsCtx);
  const ref = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const pick = async (f?: File) => { if (!f) return; setBusy(true); const url = await uploadImage(f, "logo", live); setBusy(false); if (ref.current) ref.current.value = ""; if (url) onChange(url); };
  return (
    <div className="ws-logo">
      <div className={"ws-logo-box" + (dark ? " dark" : "")}>{value ? <img src={value} alt={label} /> : <span className="faint" style={{ fontSize: 12 }}>No {label.toLowerCase()}</span>}</div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <b style={{ fontSize: 13 }}>{label}</b>
        <div className="ws-hint">{hint}</div>
        <div className="row gap-8" style={{ marginTop: 6, flexWrap: "wrap" }}>
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" style={{ display: "none" }} onChange={(e: any) => pick(e.target.files && e.target.files[0])} />
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => (live ? ref.current && ref.current.click() : notify(OFFLINE_MSG))}>{busy ? "Uploading…" : value ? "Replace" : "Upload"}</button>
          {value && <button type="button" className="rv-link" onClick={() => onChange(undefined)}>Remove</button>}
        </div>
      </div>
    </div>
  );
}
export function DesignTab({ d, set, store, live }: TabProps) {
  const colors = d.brand.colors;
  const pk = printKitColors(store && store.settings);
  const presets = pk ? [...COLOR_PRESETS, { id: "printkit", label: "My Print Kit", colors: pk }] : COLOR_PRESETS;
  const current = presets.find((p) => stable(p.colors) === stable(colors));
  const companyLogo = store && store.company && store.company.logo;
  const [busy, setBusy] = React.useState(false);
  const useCompanyLogo = async () => {
    if (!live) { notify(OFFLINE_MSG); return; }
    setBusy(true);
    try {
      const DB: any = (window as any).VFP_DB;
      const r = await DB.siteUpload(companyLogo, "logo");
      set("brand.logo", { ...d.brand.logo, onLight: r.url, onDark: d.brand.logo.onDark || r.url });
      notify("Logo added from Settings.");
    } catch (e: any) { notify(e.message || "Couldn't copy the logo", { kind: "error" }); }
    setBusy(false);
  };
  return (
    <>
      <TabHead title="Design" sub="Your logo, colors and font. The layout is the proven Kodiak design — it doesn't change, so it always looks right on a phone." />
      <Group title="Logo" right={companyLogo && !d.brand.logo.onLight && !d.brand.logo.onDark ? <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={useCompanyLogo}>{busy ? "Copying…" : "Use my logo from Settings"}</button> : null}>
        <div className="ws-list">
          {LOGO_SLOTS.map(([k, label, hint, dark]) => (
            <LogoSlot key={k} k={k} label={label} hint={hint} dark={dark} value={(d.brand.logo as any)[k]} onChange={(v: any) => set("brand.logo." + k, v)} />
          ))}
        </div>
        <p className="ws-note" style={{ marginTop: 8 }}>No logo? Your short name is set as a clean text wordmark.</p>
      </Group>
      <Group title="Colors" sub={current ? current.label : "Custom"}>
        <div className="ws-presets">
          {presets.map((p) => (
            <button key={p.id} type="button" className={"ws-preset" + (current && current.id === p.id ? " on" : "")} onClick={() => set("brand.colors", { ...p.colors })}>
              <span className="ws-swatch" style={{ background: p.colors.ink }}><i style={{ background: p.colors.accent }} /><em style={{ background: p.colors.cream }} /></span>
              {p.label}
            </button>
          ))}
        </div>
        <div className="ws-colors">
          {COLOR_FIELDS.map(([k, label, hint]) => (
            <label key={k} className="ws-colorrow">
              <input type="color" className="ws-color" value={colors[k]} onChange={(e: any) => set("brand.colors." + k, e.target.value.toUpperCase())} />
              <span style={{ minWidth: 0 }}><b>{label}</b><span className="ws-hint" style={{ display: "block", marginTop: 0 }}>{hint}</span></span>
              <input className="input ws-input ws-hex" value={colors[k]} aria-label={label + " hex"} onChange={(e: any) => { const v = e.target.value.trim(); if (/^#?[0-9a-f]{0,6}$/i.test(v)) set("brand.colors." + k, (v.startsWith("#") ? v : "#" + v).toUpperCase()); }} />
            </label>
          ))}
        </div>
        <ContrastNote colors={colors} />
      </Group>
      <Group title="Font">
        <SelectField label="Headings & text" value={d.brand.font} onChange={(v: any) => set("brand.font", v)} options={FONTS.map((f) => [f.id, f.label])} />
      </Group>
    </>
  );
}
function lum(hex: string) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || ""); if (!m) return 0.5;
  const n = parseInt(m[1], 16); const c = [n >> 16, (n >> 8) & 255, n & 255].map((v) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}
const contrast = (a: string, b: string) => { const x = lum(a), y = lum(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); };
function ContrastNote({ colors }: any) {
  const warn: string[] = [];
  if (contrast(colors.body, colors.cream) < 4.5) warn.push("body text is hard to read on the background");
  if (contrast("#FFFFFF", colors.accent) < 2.6) warn.push("white button text is hard to read on the accent");
  if (contrast("#FFFFFF", colors.ink) < 7) warn.push("the dark sections aren't dark enough for white text");
  if (!warn.length) return null;
  return <div className="ws-warn"><Icon name="bolt" size={13} />Heads up: {warn.join("; ")}.</div>;
}

/* ---------------- Home ---------------- */
export function HomeTab({ d, set, bind, ai, aiBusy }: TabProps) {
  const h = d.home;
  return (
    <>
      <TabHead title="Home page" sub="The first thing a homeowner sees. Say where you work, what you build and why you — in one screen.">
        <AiButton busy={aiBusy === "home"} onClick={() => ai({ section: "home", label: "Home page", current: h, apply: (v: any) => set("home", { ...h, ...v, heroPhoto: h.heroPhoto, gallery: h.gallery }) })} />
      </TabHead>
      <Group title="Hero">
        <TextField label="Eyebrow" hint="The small line above the headline — town, state and what you install." {...bind("home.eyebrow")} max={90} />
        <TextArea label="Headline" rows={2} max={70} {...bind("home.h1")} />
        <TextArea label="Intro" rows={3} max={220} {...bind("home.lead")} />
        <PhotoField label="Hero photo" hint="Wide shot of a finished fence you built. Landscape, at least 1600 px wide." value={h.heroPhoto} onChange={(p) => set("home.heroPhoto", p || { src: "", alt: "" })} />
      </Group>
      <Group title="Recent work">
        <PhotoListEditor label="Your job photos" hint="Shows as “Recent work” on your home page once you have 3 or more, in rows of three. Your own jobs only." value={h.gallery || []} onChange={(v) => set("home.gallery", v)} />
      </Group>
      <Group title="Why you">
        {two(<TextField label="Section label" {...bind("home.whyTitle")} />, <TextField label="Heading (optional)" {...bind("home.whyHeading")} />)}
        <CardListEditor label="Reasons" value={h.why} onChange={(v) => set("home.why", v)} addLabel="Add a reason" />
      </Group>
      <Group title="How it works" defaultOpen={false}>
        <StepListEditor label="Steps" value={h.process} onChange={(v) => set("home.process", v)} />
      </Group>
      <Group title="Styles & service area" defaultOpen={false}>
        <TextField label="Styles section title" {...bind("home.servicesTitle")} />
        <TextField label="Service area title" {...bind("home.areaTitle")} />
        <TextArea label="Service area blurb" rows={2} {...bind("home.areaBlurb")} />
      </Group>
      <Group title="Financing teaser" defaultOpen={false}>
        <Toggle label="Show a financing band on the home page" value={!!h.financingTeaser} onChange={(on: boolean) => set("home.financingTeaser", on ? { title: "Low monthly payments", text: "Split your fence into monthly payments." } : undefined)} />
        {h.financingTeaser && <>{<TextField label="Title" {...bind("home.financingTeaser.title")} />}<TextArea label="Text" rows={2} {...bind("home.financingTeaser.text")} /></>}
      </Group>
      <Group title="Call-to-action band" sub="optional" defaultOpen={false}>
        <p className="ws-note">The accent-colored band at the foot of most pages. Leave blank for the standard wording.</p>
        <TextField label="Heading" value={(h.cta && h.cta.heading) || ""} onChange={(v: string) => set("home.cta", v || (h.cta && h.cta.sub) ? { heading: v, sub: (h.cta && h.cta.sub) || "" } : undefined)} />
        <TextField label="Subline" value={(h.cta && h.cta.sub) || ""} onChange={(v: string) => set("home.cta", v || (h.cta && h.cta.heading) ? { heading: (h.cta && h.cta.heading) || "", sub: v } : undefined)} />
      </Group>
    </>
  );
}

/* ---------------- Styles ---------------- */
function blankStyle(name: string, slug: string): FenceStyle {
  const P = { src: "", alt: "" };
  return { slug, name, on: true, tag: "", heights: "", thumb: P, heroPhoto: P, gallery: [], tagline: "", intro: "", features: [], materials: [], bestFor: [], overview: [], process: [], climate: [], climateTitle: "", costFactors: [], care: [], faqs: [], price: "" };
}
function StyleEditor({ s, i, set, ai, aiBusy }: { s: FenceStyle; i: number; set: any; ai: any; aiBusy: string }) {
  const p = (k: string) => "styles.items." + i + "." + k;
  const b = (k: string) => ({ value: (s as any)[k], onChange: (v: any) => set(p(k), v) });
  return (
    <div className="ws-sub">
      <div className="row between" style={{ gap: 8, flexWrap: "wrap" }}>
        <span className="faint" style={{ fontSize: 12 }}>/styles/{s.slug}</span>
        <AiButton busy={aiBusy === "style:" + s.slug} onClick={() => ai({ section: "style:" + s.slug, label: s.name, current: s, answers: { styles: [s.slug] },
          apply: (v: any) => set("styles.items." + i, { ...s, ...v, slug: s.slug, on: s.on, thumb: s.thumb, heroPhoto: s.heroPhoto, gallery: s.gallery }) })} />
      </div>
      <div className="ws-3">
        <TextField label="Name" {...b("name")} />
        <TextField label="Badge" placeholder="Most Popular" {...b("tag")} />
        <TextField label="Heights" placeholder="4–8 ft" {...b("heights")} />
      </div>
      <TextField label="Tagline" {...b("tagline")} max={110} />
      <TextArea label="Intro" rows={3} {...b("intro")} />
      <TextField label="Price line" placeholder="Most projects land around $35–$55 per ft installed" hint="A range, never a promise. Your real signed-quote prices make the best range." {...b("price")} />
      <Group title="Photos" defaultOpen={false}>
        <PhotoField label="Hero photo" value={s.heroPhoto} onChange={(v) => set(p("heroPhoto"), v || { src: "", alt: "" })} />
        <PhotoField label="Card thumbnail" compact value={s.thumb} onChange={(v) => set(p("thumb"), v || { src: "", alt: "" })} />
        <PhotoListEditor label="Gallery" value={s.gallery} onChange={(v) => set(p("gallery"), v)} />
      </Group>
      <Group title="Features & materials" defaultOpen={false}>
        <StringListEditor label="Features" {...b("features")} addLabel="Add a feature" />
        <StringListEditor label="Materials" {...b("materials")} addLabel="Add a material" />
        <StringListEditor label="Best for" {...b("bestFor")} addLabel="Add one" />
      </Group>
      <Group title="Overview & process" defaultOpen={false}>
        <StringListEditor label="Overview paragraphs" multiline rows={4} {...b("overview")} addLabel="Add a paragraph" />
        <StepListEditor label="How you install it" value={s.process} onChange={(v) => set(p("process"), v)} />
      </Group>
      <Group title="Climate" defaultOpen={false}>
        <TextField label="Section title" placeholder="Built for Wisconsin winters" {...b("climateTitle")} />
        <CardListEditor label="Why it holds up here" value={s.climate} onChange={(v) => set(p("climate"), v)} />
      </Group>
      <Group title="Cost, care & FAQs" defaultOpen={false}>
        <StringListEditor label="What moves the price" {...b("costFactors")} addLabel="Add a factor" />
        <StringListEditor label="Care tips" {...b("care")} addLabel="Add a tip" />
        <FaqEditor value={s.faqs} onChange={(v) => set(p("faqs"), v)} />
      </Group>
    </div>
  );
}
const exampleCount = (x: any): number => {
  if (!x || typeof x !== "object") return 0;
  if (Array.isArray(x)) return x.reduce((a, y) => a + exampleCount(y), 0);
  if (typeof x.src === "string" && "alt" in x) return x.example && x.src ? 1 : 0;
  return Object.keys(x).reduce((a, k) => a + exampleCount(x[k]), 0);
};
export function StylesTab({ d, set, bind, ai, aiBusy, sub, setSub }: TabProps) {
  const items = d.styles.items;
  const [adding, setAdding] = React.useState("");
  const add = () => {
    const name = adding.trim(); if (!name) return;
    const slug = slugify(name);
    if (!slug || items.some((s) => s.slug === slug)) { notify("You already have a style called that.", { kind: "error" }); return; }
    set("styles.items", [...items, blankStyle(name, slug)]); setAdding(""); setSub(slug);
    notify("Style added — fill it in, or press Rewrite with AI to draft it.");
  };
  const remove = async (s: FenceStyle) => {
    if (!(await confirmDialog({ title: `Delete ${s.name}?`, body: "Its page and everything on it goes away. To hide it for now, switch it off instead.", confirmLabel: "Delete", danger: true }))) return;
    set("styles.items", items.filter((x) => x.slug !== s.slug)); if (sub === s.slug) setSub(null);
  };
  return (
    <>
      <TabHead title="Fence styles" sub="One page per style you install. Switch off what you don't sell — it drops out of the menu and the sitemap." />
      <div className="ws-rows">
        {items.map((s, i) => {
          const open = sub === s.slug;
          const ex = exampleCount(s);
          return (
            <div key={s.slug} className={"ws-row" + (open ? " open" : "") + (s.on ? "" : " off")}>
              <div className="ws-row-head">
                <button type="button" className={"sw" + (s.on ? " on" : "")} role="switch" aria-checked={s.on} aria-label={(s.on ? "Hide " : "Show ") + s.name} onClick={() => set("styles.items." + i + ".on", !s.on)}><span className="knob" /></button>
                <button type="button" className="ws-row-main" onClick={() => setSub(open ? null : s.slug)} aria-expanded={open}>
                  <span className="ws-row-thumb">{s.thumb && s.thumb.src ? <img src={s.thumb.src} alt="" loading="lazy" /> : <Icon name="image" size={16} />}</span>
                  <span style={{ minWidth: 0 }}><b>{s.name || "Untitled style"}</b>{s.tag && <span className="chip teal" style={{ marginLeft: 8, fontSize: 10.5, padding: "1px 7px" }}>{s.tag}</span>}
                    <span className="ws-row-sub">{s.on ? (s.tagline || "No tagline yet") : "Hidden from your site"}{ex ? ` · ${ex} example photo${ex === 1 ? "" : "s"}` : ""}</span></span>
                  <Icon name="chevdown" size={15} style={{ transform: open ? "rotate(180deg)" : "none", marginLeft: "auto", flex: "0 0 auto" }} />
                </button>
              </div>
              {open && (
                <>
                  <StyleEditor s={s} i={i} set={set} ai={ai} aiBusy={aiBusy} />
                  <div className="ws-row-foot"><button type="button" className="rv-link" style={{ color: "var(--red)" }} onClick={() => remove(s)}>Delete this style</button></div>
                </>
              )}
            </div>
          );
        })}
      </div>
      <div className="ws-addrow">
        <input className="input ws-input" value={adding} placeholder="Add a style you install — e.g. Farm & ranch" onChange={(e: any) => setAdding(e.target.value)} onKeyDown={(e: any) => e.key === "Enter" && add()} />
        <button type="button" className="btn btn-ghost btn-sm" disabled={!adding.trim()} onClick={add}><Icon name="plus" size={13} />Add style</button>
      </div>
      <Group title="Styles overview page" defaultOpen={false}>
        <TextArea label="Why it holds up here — intro (optional)" rows={2} {...bind("styles.climateIntro")} />
        <ObjListEditor label="Buyer's guide" value={d.styles.guide} onChange={(v) => set("styles.guide", v)} noun="guide card" addLabel="Add a guide card"
          fields={[{ k: "n", label: "No.", width: 64 }, { k: "title", label: "Question" }, { k: "recLabel", label: "Go with", width: 90 }, { k: "rec", label: "Recommendation" }, { k: "desc", label: "Explanation", kind: "area" }]}
          blank={(xs) => ({ n: String(xs.length + 1).padStart(2, "0"), title: "", desc: "" })} />
        <ObjListEditor label="Comparison table" value={d.styles.compare} onChange={(v) => set("styles.compare", v)} noun="row" addLabel="Add a row"
          fields={[{ k: "name", label: "Style" }, { k: "privacy", label: "Privacy", width: 90 }, { k: "maintenance", label: "Upkeep" }, { k: "lifespan", label: "Lifespan", width: 90 }, { k: "cost", label: "Cost", width: 64 }, { k: "bestFor", label: "Best for" }, { k: "popular", label: "Popular", kind: "bool" }, { k: "maintGood", label: "Low upkeep", kind: "bool" }]}
          blank={() => ({ slug: "", name: "", popular: false, privacy: "", maintenance: "", maintGood: false, lifespan: "", cost: "", bestFor: "" })} />
        <FaqEditor value={d.styles.faqs} onChange={(v) => set("styles.faqs", v)} />
      </Group>
    </>
  );
}

/* ---------------- Service area ---------------- */
function blankCity(name: string, county = ""): CityPage {
  return { slug: slugify(name), name, on: true, county, drive: "", popular: [], tagline: `Fence installation in ${name}`, intro: [""], why: "", neighborhoods: [], faqs: [] };
}
function CityEditor({ c, i, d, set, ai, aiBusy }: { c: CityPage; i: number; d: SiteContent; set: any; ai: any; aiBusy: string }) {
  const p = (k: string) => "areas.cities." + i + "." + k;
  const b = (k: string) => ({ value: (c as any)[k], onChange: (v: any) => set(p(k), v) });
  return (
    <div className="ws-sub">
      <div className="row between" style={{ gap: 8, flexWrap: "wrap" }}>
        <span className="faint" style={{ fontSize: 12 }}>/locations/{c.slug}</span>
        <AiButton busy={aiBusy === "city:" + c.slug} onClick={() => ai({ section: "city:" + c.slug, label: c.name, current: c, answers: { cities: [c.name] },
          apply: (v: any) => set("areas.cities." + i, { ...c, ...v, slug: c.slug, on: c.on, name: c.name }) })} />
      </div>
      <div className="ws-3">
        <TextField label="Town" {...b("name")} />
        <TextField label="County" {...b("county")} />
        <TextField label="Drive" placeholder="~20 min from our shop" {...b("drive")} />
      </div>
      <TextField label="Tagline" {...b("tagline")} max={110} />
      <StringListEditor label="Intro paragraphs" multiline rows={3} {...b("intro")} addLabel="Add a paragraph" />
      <TextArea label="Why it matters here" hint="Local facts only — soil, frost, HOA rules, lot sizes. Specifics are what make a town page rank." rows={3} {...b("why")} />
      <ChipsInput label="Neighborhoods" placeholder="Type a neighborhood, press Enter" {...b("neighborhoods")} />
      <Field label="Popular styles here">
        <div className="ws-checks">
          {d.styles.items.map((s) => (
            <label key={s.slug} className="ws-check"><input type="checkbox" checked={c.popular.includes(s.slug)}
              onChange={(e: any) => set(p("popular"), e.target.checked ? [...c.popular, s.slug] : c.popular.filter((x) => x !== s.slug))} />{s.name}</label>
          ))}
        </div>
      </Field>
      <PhotoListEditor label={`Your photos from ${c.name || "this town"}`} hint="Jobs you finished here. They show as “Recent projects” on the page." value={c.photos || []} onChange={(v) => set(p("photos"), v)} />
      {(c.reviews || []).length > 0 && (
        <Field label={`What ${c.name} customers say`} hint="Quotes from your public reviews that name the town, as they wrote them.">
          <div className="ws-list">
            {(c.reviews || []).map((r, j) => (
              <div key={j} className="ws-obj-row">
                <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>“{r.text}” <span className="faint">— {r.author}</span></span>
                <button type="button" className="ws-ib danger" aria-label="Remove this quote" onClick={() => set(p("reviews"), (c.reviews || []).filter((_, k) => k !== j))}><Icon name="x" size={14} /></button>
              </div>
            ))}
          </div>
        </Field>
      )}
      <FaqEditor value={c.faqs} onChange={(v) => set(p("faqs"), v)} />
    </div>
  );
}
export function AreasTab({ d, set, bind, ai, aiBusy, sub, setSub, live }: TabProps) {
  const cities = d.areas.cities;
  const [adding, setAdding] = React.useState("");
  const add = () => {
    const name = adding.trim(); if (!name) return;
    const slug = slugify(name);
    if (!slug || cities.some((c) => c.slug === slug)) { notify(`${name} already has a page.`, { kind: "error" }); return; }
    set("areas.cities", [...cities, blankCity(name)]); setAdding(""); setSub(slug);
    notify(`${name} added — press Rewrite with AI to draft the page, then add what you know about building there.`);
  };
  const remove = async (c: CityPage) => {
    if (!(await confirmDialog({ title: `Delete the ${c.name} page?`, body: "To hide it for now, switch it off instead.", confirmLabel: "Delete", danger: true }))) return;
    set("areas.cities", cities.filter((x) => x.slug !== c.slug)); if (sub === c.slug) setSub(null);
  };
  return (
    <>
      <TabHead title="Service area" sub="Where you build, and a page for each town you want to win on Google." />
      <EarnedCities d={d} set={set} live={live} setSub={setSub} />
      <Group title="Service area page">
        <TextArea label="Intro" rows={3} {...bind("areas.intro")} />
        <TextField label="Radius" placeholder="a 90-minute radius of Green Bay" {...bind("areas.radiusText")} />
      </Group>
      <Group title="Regions" sub={d.areas.regions.length + ""} defaultOpen={false}>
        <div className="ws-list">
          {d.areas.regions.map((r, i) => (
            <div key={i} className="ws-obj">
              <div className="ws-obj-row">
                <input className="input ws-input" style={{ flex: 1 }} aria-label="Region name" placeholder="Region name" value={r.name} onChange={(e: any) => set("areas.regions." + i + ".name", e.target.value)} />
                <button type="button" className="ws-ib danger" aria-label="Remove region" onClick={() => set("areas.regions", d.areas.regions.filter((_, j) => j !== i))}><Icon name="x" size={14} /></button>
              </div>
              <input className="input ws-input" aria-label="Region blurb" placeholder="One line about it" value={r.blurb} onChange={(e: any) => set("areas.regions." + i + ".blurb", e.target.value)} />
              <ObjListEditor value={r.cities} onChange={(v) => set("areas.regions." + i + ".cities", v)} noun="town" addLabel="Add a town"
                fields={[{ k: "name", label: "Town" }, { k: "note", label: "Note", placeholder: "~20 min" }]} blank={() => ({ name: "", note: "" })} />
            </div>
          ))}
          <button type="button" className="ws-add" onClick={() => set("areas.regions", [...d.areas.regions, { name: "", blurb: "", cities: [] }])}><Icon name="plus" size={13} />Add a region</button>
        </div>
      </Group>
      <div className="ws-sechead"><b>Town pages</b><span className="faint"> · {cities.filter((c) => c.on).length} live</span></div>
      <div className="ws-rows">
        {cities.map((c, i) => {
          const open = sub === c.slug;
          return (
            <div key={c.slug} className={"ws-row" + (open ? " open" : "") + (c.on ? "" : " off")}>
              <div className="ws-row-head">
                <button type="button" className={"sw" + (c.on ? " on" : "")} role="switch" aria-checked={c.on} aria-label={(c.on ? "Hide " : "Show ") + c.name} onClick={() => set("areas.cities." + i + ".on", !c.on)}><span className="knob" /></button>
                <button type="button" className="ws-row-main" onClick={() => setSub(open ? null : c.slug)} aria-expanded={open}>
                  <span style={{ minWidth: 0 }}><b>{c.name}</b><span className="ws-row-sub">{[c.county, c.drive].filter(Boolean).join(" · ") || (c.why ? "" : "Not written yet")}{!c.on ? " · hidden" : ""}</span></span>
                  <Icon name="chevdown" size={15} style={{ transform: open ? "rotate(180deg)" : "none", marginLeft: "auto", flex: "0 0 auto" }} />
                </button>
              </div>
              {open && <><CityEditor c={c} i={i} d={d} set={set} ai={ai} aiBusy={aiBusy} /><div className="ws-row-foot"><button type="button" className="rv-link" style={{ color: "var(--red)" }} onClick={() => remove(c)}>Delete this page</button></div></>}
            </div>
          );
        })}
      </div>
      <div className="ws-addrow">
        <input className="input ws-input" value={adding} placeholder="Add a town — e.g. Suamico" onChange={(e: any) => setAdding(e.target.value)} onKeyDown={(e: any) => e.key === "Enter" && add()} />
        <button type="button" className="btn btn-ghost btn-sm" disabled={!adding.trim()} onClick={add}><Icon name="plus" size={13} />Add town page</button>
      </div>
      <p className="ws-note">Add towns you actually work in. A handful of real, specific town pages beats fifty thin ones — Google filters look-alike pages.</p>
      <Group title="Service area FAQs" defaultOpen={false}><FaqEditor value={d.areas.faqs} onChange={(v) => set("areas.faqs", v)} /></Group>
    </>
  );
}

/* ---------------- About ---------------- */
export function AboutTab({ d, set, bind, ai, aiBusy }: TabProps) {
  const a = d.about;
  return (
    <>
      <TabHead title="About" sub="Who you are, in your words. People hire the owner — put your name and face on it.">
        <AiButton busy={aiBusy === "about"} onClick={() => ai({ section: "about", label: "About page", current: a, apply: (v: any) => set("about", { ...a, ...v, photo: a.photo, owner: v.owner ? { ...v.owner, photo: a.owner && a.owner.photo } : a.owner }) })} />
      </TabHead>
      <Group title="Story">
        <TextArea label="Headline" rows={2} {...bind("about.h1")} max={90} />
        <TextArea label="Intro (optional)" rows={2} {...bind("about.lead")} />
        <TextField label="Story heading (optional)" {...bind("about.storyTitle")} />
        <StringListEditor label="Your story" multiline rows={4} {...bind("about.story")} addLabel="Add a paragraph" />
        <PhotoField label="Photo" hint="You, your crew or your trucks. Real beats polished." allowClear value={a.photo} onChange={(v) => set("about.photo", v)} />
      </Group>
      <Group title="Owner" defaultOpen={!!a.owner}>
        <Toggle label="Show an owner quote" value={!!a.owner} onChange={(on: boolean) => set("about.owner", on ? { name: d.business.ownerName || "", title: "Owner", quote: "" } : undefined)} />
        {a.owner && <>
          {two(<TextField label="Name" {...bind("about.owner.name")} />, <TextField label="Title" {...bind("about.owner.title")} />)}
          <TextArea label="Quote" rows={2} {...bind("about.owner.quote")} />
          <TextArea label="A few more words (optional)" rows={3} {...bind("about.owner.text")} />
          <PhotoField label="Owner photo" allowClear compact value={a.owner.photo} onChange={(v) => set("about.owner.photo", v)} />
        </>}
      </Group>
      <Group title="By the numbers" defaultOpen={false}>
        <ObjListEditor value={a.stats} onChange={(v) => set("about.stats", v)} noun="stat" addLabel="Add a number" hint="Only numbers you can back up."
          fields={[{ k: "big", label: "Number", width: 100, placeholder: "1,200+" }, { k: "small", label: "What it counts", placeholder: "fences installed" }]} blank={() => ({ big: "", small: "" })} />
      </Group>
      <Group title="Standards" defaultOpen={false}>
        <CardListEditor label="Corners we never cut" value={a.neverCut} onChange={(v) => set("about.neverCut", v)} />
        <CardListEditor label="Our standard" value={a.standard} onChange={(v) => set("about.standard", v)} />
      </Group>
      <Group title="FAQs" defaultOpen={false}><FaqEditor value={a.faqs} onChange={(v) => set("about.faqs", v)} /></Group>
    </>
  );
}

/* ---------------- Financing & cost ---------------- */
export function FinancingTab({ d, set, bind }: TabProps) {
  const f = d.financing;
  const calc = f.calculator;
  return (
    <>
      <TabHead title="Financing & cost" sub="Monthly payments and honest price ranges — the two pages homeowners read before they call." />
      <Group title="Financing page">
        {two(<TextField label="Lender" placeholder="Wisetack" {...bind("financing.partnerName")} />, <TextField label="Apply link" placeholder="https://…" {...bind("financing.applyUrl")} />)}
        <CardListEditor label="Why finance" value={f.why} onChange={(v) => set("financing.why", v)} />
        <StepListEditor label="How it works" value={f.steps} onChange={(v) => set("financing.steps", v)} />
        <StringListEditor label="What it covers" {...bind("financing.covered")} addLabel="Add one" />
        <Field label="Payment calculator" hint="APR is a percentage (9.99 = 9.99%). Terms in months, amounts in dollars.">
          <div className="ws-3">
            <NumField label="APR %" step={0.01} value={calc.apr} onChange={(v: number) => set("financing.calculator.apr", v)} />
            <TextField label="Terms" value={(calc.terms || []).join(", ")} onChange={(v: string) => set("financing.calculator.terms", v.split(/[^0-9]+/).filter(Boolean).map(Number))} />
            <NumField label="Starting amount" value={calc.start} onChange={(v: number) => set("financing.calculator.start", v)} />
          </div>
          <div className="ws-2">
            <NumField label="Minimum" value={calc.min} onChange={(v: number) => set("financing.calculator.min", v)} />
            <NumField label="Maximum" value={calc.max} onChange={(v: number) => set("financing.calculator.max", v)} />
          </div>
        </Field>
        <FaqEditor value={f.faqs} onChange={(v) => set("financing.faqs", v)} />
      </Group>
      <Group title="Cost guide page">
        <TextArea label="Intro" rows={3} {...bind("cost.intro")} />
        <CardListEditor label="What moves the price" value={d.cost.factors} onChange={(v) => set("cost.factors", v)} addLabel="Add a factor" />
        <ObjListEditor label="Sample projects" value={d.cost.samples} onChange={(v) => set("cost.samples", v)} noun="sample" addLabel="Add a sample project" hint="Real jobs, rounded. Low and high in dollars."
          fields={[{ k: "title", label: "Project" }, { k: "low", label: "Low $", kind: "num", width: 92 }, { k: "high", label: "High $", kind: "num", width: 92 }, { k: "detail", label: "Detail — 150 ft of 6 ft vinyl, one gate", kind: "area", rows: 1 }]}
          blank={() => ({ title: "", detail: "", low: 0, high: 0 })} />
      </Group>
    </>
  );
}

/* ---------------- Repair & gates ---------------- */
export function RepairTab({ d, set, bind }: TabProps) {
  return (
    <>
      <TabHead title="Repair & gates" sub="Small jobs bring in new customers. These pages catch “fence repair near me”." />
      <Group title="Fence repair page">
        <TextArea label="Intro" rows={3} {...bind("repair.intro")} />
        <CardListEditor label="Repairs you do" value={d.repair.repairs} onChange={(v) => set("repair.repairs", v)} addLabel="Add a repair" />
        <StepListEditor label="How a repair goes" value={d.repair.process} onChange={(v) => set("repair.process", v)} />
        {two(<PhotoField label="Before" compact allowClear value={d.repair.before} onChange={(v) => set("repair.before", v)} />, <PhotoField label="After" compact allowClear value={d.repair.after} onChange={(v) => set("repair.after", v)} />)}
        <FaqEditor value={d.repair.faqs} onChange={(v) => set("repair.faqs", v)} />
      </Group>
      <Group title="Gates page">
        <TextArea label="Intro" rows={3} {...bind("gates.intro")} />
        <CardListEditor label="Gate types" value={d.gates.types} onChange={(v) => set("gates.types", v)} addLabel="Add a gate type" />
        <CardListEditor label="Openers" value={d.gates.operators} onChange={(v) => set("gates.operators", v)} addLabel="Add an opener" />
        <FaqEditor value={d.gates.faqs} onChange={(v) => set("gates.faqs", v)} />
      </Group>
    </>
  );
}

/* ---------------- Care ---------------- */
export function CareTab({ d, set }: TabProps) {
  const mats = d.care.materials;
  return (
    <>
      <TabHead title="Fence care" sub="A care guide builds trust after the sale — and ranks for “how to clean a vinyl fence”." />
      <Group title="Care guide">
        <CardListEditor label="Ground rules" value={d.care.rules} onChange={(v) => set("care.rules", v)} addLabel="Add a rule" />
        <Field label="By material">
          <div className="ws-list">
            {mats.map((m, i) => (
              <div key={i} className="ws-obj">
                <div className="ws-obj-row">
                  <input className="input ws-input" style={{ flex: 1 }} placeholder="Material" aria-label="Material" value={m.name} onChange={(e: any) => set("care.materials." + i + ".name", e.target.value)} />
                  <button type="button" className="ws-ib danger" aria-label="Remove material" onClick={() => set("care.materials", mats.filter((_, j) => j !== i))}><Icon name="x" size={14} /></button>
                </div>
                <textarea className="textarea ws-input" rows={Math.max(2, m.points.length)} placeholder="One tip per line" aria-label="Tips" value={m.points.join("\n")} onChange={(e: any) => set("care.materials." + i + ".points", e.target.value.split("\n"))} />
              </div>
            ))}
            <button type="button" className="ws-add" onClick={() => set("care.materials", [...mats, { name: "", points: [] }])}><Icon name="plus" size={13} />Add a material</button>
          </div>
        </Field>
        <StepListEditor label="Season by season" nLabel="Season" value={d.care.seasons} onChange={(v) => set("care.seasons", v)} addLabel="Add a season" />
        <FaqEditor value={d.care.faqs} onChange={(v) => set("care.faqs", v)} />
      </Group>
    </>
  );
}

/* ---------------- Contact & referrals ---------------- */
export function ContactTab({ d, set, bind }: TabProps) {
  return (
    <>
      <TabHead title="Contact & referrals" sub="What happens after someone reaches out, how neighbors refer each other, and how you ask for reviews." />
      <Group title="Contact page">
        <StepListEditor label="What happens next" value={d.contact.nextSteps} onChange={(v) => set("contact.nextSteps", v)} />
        <FaqEditor value={d.contact.faqs} onChange={(v) => set("contact.faqs", v)} />
      </Group>
      <Group title="Refer a neighbor" defaultOpen={false}>
        <StepListEditor label="How it works" value={d.refer.steps} onChange={(v) => set("refer.steps", v)} />
        <CardListEditor label="Our promises" value={d.refer.promises} onChange={(v) => set("refer.promises", v)} />
        <TextField label="Referral thank-you (optional)" placeholder="$100 Visa gift card when their fence is installed" hint="Thanks for a referral is fine. Never offer anything in exchange for a review." {...bind("refer.reward")} />
      </Group>
      <Group title="Leave a review page" defaultOpen={false}>
        <StepListEditor label="Steps" value={d.reviewAsk.steps} onChange={(v) => set("reviewAsk.steps", v)} />
      </Group>
    </>
  );
}

/* ---------------- Blog ---------------- */
const CATEGORIES = ["Installation", "Planning", "Materials", "Pricing", "Maintenance", "Safety", "Hiring Guide", "Commercial"];
function blankPost(): Post {
  const iso = new Date().toISOString().slice(0, 10);
  return { slug: "", title: "", category: "Planning", date: longDate(iso), dateISO: iso, read: 4, excerpt: "", body: [{ t: "p", x: "" }] };
}
function PostEditor({ p, i, d, set, onBack }: { p: Post; i: number; d: SiteContent; set: any; onBack: () => void }) {
  const at = (k: string) => "posts." + i + "." + k;
  const b = (k: string) => ({ value: (p as any)[k], onChange: (v: any) => set(at(k), v) });
  const words = (p.body || []).reduce((a, x: any) => a + String(x.x || (x.items || []).join(" ")).split(/\s+/).filter(Boolean).length, 0);
  const slugTaken = !!p.slug && d.posts.some((x, j) => j !== i && x.slug === p.slug);
  return (
    <div className="ws-sub" style={{ borderTop: 0, paddingTop: 0 }}>
      <button type="button" className="btn btn-ghost btn-sm" style={{ justifySelf: "start" }} onClick={onBack}><Icon name="chevright" size={14} style={{ transform: "rotate(180deg)" }} />All posts</button>
      <TextArea label="Title" rows={2} max={70} value={p.title} onChange={(v: string) => { set(at("title"), v); if (!p.slug || p.slug === slugify(p.title)) set(at("slug"), slugify(v)); }} />
      <div className="ws-3">
        <TextField label="Link" {...b("slug")} hint={slugTaken ? <span style={{ color: "var(--red)" }}>Another post uses this link.</span> : "/blog/" + (p.slug || "…")} />
        <SelectField label="Category" {...b("category")} options={CATEGORIES.includes(p.category) ? CATEGORIES : [p.category, ...CATEGORIES]} />
        <TextField label="Date" type="date" value={p.dateISO} onChange={(v: string) => { set(at("dateISO"), v); set(at("date"), longDate(v)); }} />
      </div>
      <TextArea label="Summary" hint="Shows on the blog list and in Google results." rows={2} max={160} {...b("excerpt")} />
      <PhotoField label="Cover photo" allowClear value={p.image} onChange={(v) => set(at("image"), v)} />
      {Array.isArray(p.gallery) && <PhotoListEditor label="Photos from the job" value={p.gallery} onChange={(v) => set(at("gallery"), v)} />}
      <Field label={`Article · about ${Math.max(1, Math.round(words / 220))} min read`} right={<button type="button" className="rv-link" onClick={() => set(at("read"), Math.max(1, Math.round(words / 220)))}>Update read time ({p.read} min)</button>}>
        <BlockEditor value={p.body} onChange={(v) => set(at("body"), v)} />
      </Field>
      <FaqEditor value={p.faqs || []} onChange={(v) => set(at("faqs"), v)} />
    </div>
  );
}
/* ---- Suggested from your jobs (server/src/sites/jobposts.js) ----
   Posts drafted from finished jobs with two or more website photos: their
   town, their fence, their photos, never the customer's name or street.
   "Add to my blog" puts it in the draft like any edit — it goes live when they
   publish, and the first one is what turns the Blog page on. */
const inlineText = (s: string) => String(s || "").split(/(\[[^\]\n]{1,200}\]\([^)\s]{1,500}\))/g).map((part, i) => {
  const m = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
  return m ? <span key={i} className="ws-plink" title={m[2]}>{m[1]}</span> : <React.Fragment key={i}>{part}</React.Fragment>;
});
function PostPreview({ post }: { post: Post }) {
  const photos = [post.image, ...(post.gallery || [])].filter((x): x is NonNullable<typeof x> => !!(x && x.src));
  return (
    <div className="ws-sugg-preview">
      {photos.length > 0 && (
        <div className="ws-sugg-photos">
          {photos.map((ph, i) => <img key={i} src={ph.src} alt={ph.alt || ""} title={ph.alt || ""} loading="lazy" />)}
        </div>
      )}
      <div className="ws-sugg-body">
        {(post.body || []).map((b: any, i: number) => b.t === "h" ? <h4 key={i}>{b.x}</h4>
          : b.t === "h3" ? <h5 key={i}>{b.x}</h5>
          : b.t === "list" ? <ul key={i}>{(b.items || []).map((x: string, j: number) => <li key={j}>{inlineText(x)}</li>)}</ul>
          : b.t === "tip" ? <p key={i} className="ws-sugg-tip">{inlineText(b.x)}</p>
          : <p key={i}>{inlineText(b.x)}</p>)}
        {(post.faqs || []).length > 0 && (
          <div className="ws-sugg-faqs">
            <h5>Questions</h5>
            {(post.faqs || []).map((f, i) => <p key={i}><b>{f.q}</b><br />{f.a}</p>)}
          </div>
        )}
      </div>
    </div>
  );
}
function SuggestionEditor({ post, onChange }: { post: Post; onChange: (p: Post) => void }) {
  const b = (k: keyof Post) => ({ value: (post as any)[k], onChange: (v: any) => onChange({ ...post, [k]: v }) });
  return (
    <div className="ws-sugg-edit">
      <TextArea label="Title" rows={2} max={70} {...b("title")} />
      <TextArea label="Summary" hint="Shows on the blog list and in Google results." rows={2} max={160} {...b("excerpt")} />
      <PhotoField label="Cover photo" allowClear value={post.image} onChange={(v) => onChange({ ...post, image: v })} />
      <PhotoListEditor label="Photos from the job" value={post.gallery || []} onChange={(v) => onChange({ ...post, gallery: v })} />
      <Field label="Article"><BlockEditor value={post.body} onChange={(v) => onChange({ ...post, body: v })} /></Field>
      <FaqEditor value={post.faqs || []} onChange={(v) => onChange({ ...post, faqs: v })} />
    </div>
  );
}
const fmtShort = (iso: any) => { const t = Date.parse(iso || ""); return t ? new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""; };
function JobPostSuggestions({ d, set, live, onAdded }: { d: SiteContent; set: any; live: boolean; onAdded: (slug: string) => void }) {
  const DB: any = (window as any).VFP_DB;
  const [data, setData] = React.useState<any>(null);
  const [busy, setBusy] = React.useState("");
  const [open, setOpen] = React.useState<string | null>(null);
  const [edits, setEdits] = React.useState<Record<string, Post>>({});
  const [editing, setEditing] = React.useState<string | null>(null);
  const [pick, setPick] = React.useState("");
  const load = React.useCallback(() => {
    if (!DB || !DB.sitePostSuggestions) return;
    DB.sitePostSuggestions().then((r: any) => setData(r)).catch((e: any) => setData({ error: e.message || "Couldn't load suggestions.", code: e.code }));
  }, []);
  React.useEffect(() => { if (live) load(); }, [live]);
  if (!live) return null;   // the sample site: nothing of theirs to suggest from
  if (!data) return <Group title="Suggested from your jobs" sub="checking your finished jobs…"><p className="ws-note"><span className="ws-spin" /> Loading…</p></Group>;
  if (data.error) {
    return <Group title="Suggested from your jobs"><p className="ws-note">{data.code === "migration" ? data.message : "Couldn't load suggestions right now — " + data.error}</p></Group>;
  }
  const list: any[] = data.suggestions || [];
  const eligible: any[] = data.eligible || [];
  const cad = data.cadence || { used: 0, max: 2, left: 2 };
  const chosen = eligible.find((j) => j.workOrderId === pick) || eligible[0];
  const drop = (id: string) => setData((x: any) => ({ ...x, suggestions: (x.suggestions || []).filter((y: any) => y.id !== id) }));

  const add = async (s: any) => {
    setBusy(s.id);
    try {
      const r = await DB.sitePostAccept(s.id, edits[s.id] || undefined);
      const post: Post = r.post;
      // a link no post in THIS draft uses (the editor may hold unsaved posts)
      let slug = post.slug; let k = 2;
      while (d.posts.some((x) => x.slug === slug)) slug = post.slug + "-" + k++;
      const first = !d.posts.length;
      set("posts", [{ ...post, slug }, ...d.posts]);
      drop(s.id); setEditing(null);
      notify(first ? "Added. Your Blog page goes live with it when you publish." : "Added to your blog. Publish to put it live.", { kind: "success" });
      onAdded(slug);
    } catch (e: any) { notify(e.message || "Couldn't add it — try again.", { kind: "error" }); }
    setBusy("");
  };
  const dismiss = async (s: any) => {
    if (!(await confirmDialog({ title: "Dismiss this post?", body: "It won't be suggested again, and no other post is drafted from this job.", confirmLabel: "Dismiss" }))) return;
    setBusy(s.id);
    try { await DB.sitePostDismiss(s.id); drop(s.id); } catch (e: any) { notify(e.message || "Couldn't dismiss it — try again.", { kind: "error" }); }
    setBusy("");
  };
  const draft = async () => {
    setBusy("draft");
    try {
      const r = await DB.sitePostDraft(chosen ? chosen.workOrderId : undefined);
      setOpen(r.suggestion.id);
      load();
    } catch (e: any) { notify(e.message || "Couldn't draft a post — try again.", { kind: "error" }); if (e.code === "cadence" || e.code === "already") load(); }
    setBusy("");
  };

  const sub = list.length ? list.length + " waiting" : cad.used + " of " + cad.max + " this month";
  return (
    <Group title="Suggested from your jobs" sub={sub}>
      <p className="ws-note">Drafted from finished jobs with two or more photos: the town, the fence and your photos. Never the customer's name or street. Nothing goes on your site until you add it and publish.</p>
      {list.map((s) => {
        const p: Post = edits[s.id] || s.post;
        const n = [p.image, ...(p.gallery || [])].filter((x) => x && x.src).length;
        return (
          <div key={s.id} className={"ws-sugg" + (open === s.id || editing === s.id ? " open" : "")}>
            <div className="ws-sugg-head">
              <span className="ws-row-thumb">{p.image && p.image.src ? <img src={p.image.src} alt="" loading="lazy" /> : <Icon name="book" size={16} />}</span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <b>{p.title}</b>
                <span className="ws-sugg-sub">{p.excerpt}</span>
                <span className="ws-row-sub">{n} photo{n === 1 ? "" : "s"} · {p.read} min read · drafted {fmtShort(s.created_at)}{edits[s.id] ? " · edited" : ""}</span>
              </span>
            </div>
            {editing === s.id
              ? <SuggestionEditor post={p} onChange={(v) => setEdits((x) => ({ ...x, [s.id]: v }))} />
              : open === s.id && <PostPreview post={p} />}
            <div className="ws-sugg-actions">
              <button type="button" className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => add(s)}><Icon name="plus" size={13} />{busy === s.id ? "Adding…" : "Add to my blog"}</button>
              {editing !== s.id && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(open === s.id ? null : s.id)}>{open === s.id ? "Hide preview" : "Preview"}</button>}
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setEditing(editing === s.id ? null : s.id); setOpen(null); }}><Icon name="edit" size={13} />{editing === s.id ? "Done editing" : "Edit"}</button>
              {edits[s.id] && editing !== s.id && <button type="button" className="rv-link" onClick={() => setEdits((x) => { const y = { ...x }; delete y[s.id]; return y; })}>Undo my edits</button>}
              <button type="button" className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => dismiss(s)}>Dismiss</button>
            </div>
          </div>
        );
      })}
      <div className="ws-sugg-draft">
        {eligible.length > 0 ? (
          <>
            <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
              <select className="input ws-sugg-pick" aria-label="Job to write about" value={chosen ? chosen.workOrderId : ""} onChange={(e) => setPick(e.target.value)}>
                {eligible.map((j) => <option key={j.workOrderId} value={j.workOrderId}>{j.label}{j.town ? " · " + j.town : ""} · {j.photos} photos{j.finishedAt ? " · " + fmtShort(j.finishedAt) : ""}</option>)}
              </select>
              <button type="button" className="btn btn-outline btn-sm" disabled={!!busy || !data.llm || cad.left <= 0} onClick={draft}>
                {busy === "draft" ? <><span className="ws-spin" /> Drafting… about a minute</> : <><Icon name="bolt" size={13} />Draft a post from a job</>}
              </button>
            </div>
          </>
        ) : <p className="ws-note">When a finished job has two or more photos that can go on your website, you can draft a post about it here.</p>}
        <p className="ws-hint" style={{ margin: 0 }}>
          {!data.llm ? "The AI writer isn't set up on this server yet, so posts can't be drafted." :
            cad.left <= 0 ? `${cad.used} of ${cad.max} drafted this month. The next can be drafted on ${fmtShort(cad.resetsOn + "T12:00:00")}.` :
            `${cad.used} of ${cad.max} drafted this month.` + (data.auto ? " We also draft them on our own from your newest finished jobs, a couple of weeks apart." : "")}
        </p>
      </div>
    </Group>
  );
}

export function BlogTab({ d, set, ai, aiBusy, sub, setSub, live }: TabProps) {
  const posts = d.posts;
  const idx = sub != null ? posts.findIndex((p) => p.slug === sub) : -1;
  const [editing, setEditing] = React.useState<number>(-1);
  const cur = editing >= 0 ? editing : idx;
  const open = (i: number) => { setEditing(i); setSub(posts[i] ? posts[i].slug : null); };
  const back = () => { setEditing(-1); setSub(null); };
  const addBlank = () => { set("posts", [blankPost(), ...posts]); setEditing(0); setSub(null); };
  const remove = async (i: number) => {
    const p = posts[i];
    if (!(await confirmDialog({ title: `Delete “${p.title || "this post"}”?`, body: "The page goes away when you next publish.", confirmLabel: "Delete", danger: true }))) return;
    set("posts", posts.filter((_, j) => j !== i)); back();
  };
  const writeAi = () => ai({ section: "post", label: "New blog post", current: null, apply: (v: any) => {
    const iso = new Date().toISOString().slice(0, 10);
    let slug = slugify(v.slug || v.title || "new-post"); let k = 2; while (posts.some((x) => x.slug === slug)) slug = slugify(v.slug || v.title) + "-" + k++;
    set("posts", [{ ...blankPost(), dateISO: iso, date: longDate(iso), ...v, slug }, ...posts]); setEditing(0); setSub(slug);
  } });
  if (cur >= 0 && posts[cur]) {
    return (
      <>
        <TabHead title="Blog post" sub="Plain words, real answers. Link other pages with [text](/path).">
          <button type="button" className="btn btn-danger btn-sm" onClick={() => remove(cur)}><Icon name="trash" size={13} />Delete</button>
        </TabHead>
        <PostEditor p={posts[cur]} i={cur} d={d} set={set} onBack={back} />
      </>
    );
  }
  const sorted = posts.map((p, i) => ({ p, i })).sort((a, b) => String(b.p.dateISO).localeCompare(String(a.p.dateISO)));
  return (
    <>
      <TabHead title="Blog" sub="Helpful posts answer the questions homeowners Google before they call — cost, materials, permits.">
        <AiButton busy={aiBusy === "post"} label="Write a new post" onClick={writeAi} />
        <button type="button" className="btn btn-ghost btn-sm" onClick={addBlank}><Icon name="plus" size={13} />Blank post</button>
      </TabHead>
      <JobPostSuggestions d={d} set={set} live={live} onAdded={() => { setEditing(-1); setSub(null); }} />
      {!posts.length ? <div className="ws-empty">No posts yet, so the Blog page is hidden on your site. “Write a new post” drafts one from your business in about a minute, or add one suggested from your jobs. The Blog appears once you publish it.</div> : (
        <div className="ws-rows">
          {sorted.map(({ p, i }) => (
            <button key={p.slug + i} type="button" className="ws-row ws-post" onClick={() => open(i)}>
              <span className="ws-row-thumb">{p.image && p.image.src ? <img src={p.image.src} alt="" loading="lazy" /> : <Icon name="book" size={16} />}</span>
              <span style={{ minWidth: 0, flex: 1, textAlign: "left" }}><b>{p.title || "Untitled post"}</b><span className="ws-row-sub">{p.category} · {p.date || "No date"} · {p.read} min</span></span>
              <Icon name="chevright" size={15} />
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/* ---------------- Pages & SEO ---------------- */
export function PagesTab({ d, set }: TabProps) {
  const name = d.business.name || "";
  const suffix = name ? " | " + name : "";
  const [open, setOpen] = React.useState<string | null>(null);
  return (
    <>
      <TabHead title="Pages & SEO" sub="Turn pages on or off and write what Google shows. Titles around 60 characters and descriptions around 160 show in full." />
      <div className="ws-rows">
        {PAGE_LIST.map((pg) => {
          const m = d.pages[pg.key as PageKey] || { on: true };
          const title = m.title || "";
          const full = (title || "(standard title)") + suffix;
          const isOpen = open === pg.key && m.on;
          const custom = !!(m.title || m.description);
          return (
            <div key={pg.key} className={"ws-row ws-pagerow" + (m.on ? "" : " off") + (isOpen ? " open" : "")}>
              <div className="ws-row-head">
                <button type="button" className={"sw" + (m.on ? " on" : "")} role="switch" aria-checked={m.on} aria-label={(m.on ? "Turn off " : "Turn on ") + pg.label}
                  disabled={!!pg.locked} title={pg.locked || ""} onClick={() => set("pages." + pg.key, { ...m, on: !m.on })}><span className="knob" /></button>
                <button type="button" className="ws-row-main" disabled={!m.on} aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : pg.key)}>
                  <span style={{ minWidth: 0 }}><b>{pg.label}</b><span className="ws-row-sub">{m.on ? (custom ? (m.title || "Standard title") + (m.description ? " · own description" : "") : pg.path + " · standard Google text") : pg.path + " · off — 404s and leaves the menu & sitemap"}</span></span>
                  {m.on && custom && <span className="chip teal ws-sec-chip">Custom</span>}
                  {m.on && <Icon name="chevdown" size={15} style={{ transform: isOpen ? "rotate(180deg)" : "none", marginLeft: "auto", flex: "0 0 auto" }} />}
                </button>
              </div>
              {isOpen && (
                <div className="ws-seo">
                  <Field label="Title" right={<span className={"ws-count tnum" + (full.length > 60 ? " over" : "")} title="Including the business name the site adds">{title ? full.length : "—"}/60</span>}>
                    <input className="input ws-input" value={title} placeholder="Standard title" onChange={(e: any) => set("pages." + pg.key, { ...m, title: e.target.value || undefined })} />
                  </Field>
                  <Field label="Description" right={<span className={"ws-count tnum" + ((m.description || "").length > 160 ? " over" : "")}>{(m.description || "").length}/160</span>}>
                    <textarea className="textarea ws-input" rows={3} value={m.description || ""} placeholder="Standard description" onChange={(e: any) => set("pages." + pg.key, { ...m, description: e.target.value || undefined })} />
                  </Field>
                  {title && <div className="ws-serp"><span className="ws-serp-t">{full.length > 62 ? full.slice(0, 60) + "…" : full}</span><span className="ws-serp-d">{(m.description || "").slice(0, 158)}{(m.description || "").length > 158 ? "…" : ""}</span></div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

export const exampleCountOf = exampleCount;
export { getIn };
